import { ArrowLeft, ArrowRight, ArrowUpRight, BookOpen } from "lucide-react"
import Link from "next/link"
import { ResponsiveBlogCards } from "@/components/ui/responsive-blog-cards"
import { LazyProductRecommendations } from "@/components/ui/lazy-product-recommendations"
import { AuthorBioCard } from "@/components/blog/author-bio-card"
import { PostReadDiagnoseCTA } from "@/components/blog/post-read-diagnose-cta"
import { InlineScanCta } from "@/components/blog/inline-scan-cta"
import { isWeedPost } from "@/lib/weed-identifier-cta"
import { isDiagnosisPost } from "@/lib/blog-diagnose-cta"
import { IDENTIFY_CTA } from "@/lib/identify-cta"
import { resolvePostReadSurface } from "@/lib/blog-intent"
import { AppDownloadCTA } from "@/components/ui/app-download-cta"
import { getDirectusImageUrl } from "@/lib/directus"
import { mapBlogToProductCategory } from "@/lib/blog-product-mapping"
import type { Author } from "@/lib/types/author"

// Icons are intentionally NOT part of this list: every consumer renders the
// tool's bespoke illustrated SVG via ToolIllustration(href) from
// components/tools/tool-illustration.tsx — never a lucide glyph.
export function getRelatedTools(category: string): { href: string; label: string }[] {
  const c = category.toLowerCase()
  const tools: { href: string; label: string }[] = []

  // Buying-guide pillars (robot/riding mower equipment guides) carry the
  // "buying-guide" category, which matches none of the care-task keywords
  // below and would otherwise fall through to the generic fertilizer/watering
  // pad. Surface the equipment-shopping toolset the prototype rails instead:
  // size the yard, set mowing height, estimate cost, compare grass.
  if (c.includes('buying') || c.includes('mower') || c.includes('equipment')) {
    return [
      { href: '/tools/lawn-size-calculator', label: 'Lawn Size Calculator' },
      { href: '/tools/mowing-calculator', label: 'Mowing Height Calculator' },
      { href: '/tools/lawn-cost-estimator', label: 'Lawn Cost Estimator' },
      { href: '/tools/grass-comparison', label: 'Grass Comparison Tool' },
    ]
  }

  // Pest/insect surfaces get the grub calculator, which is the only tool that
  // actually answers an insect question (region timing + how much to buy).
  // Herbicide timing is a weed tool and reads as a mismatch on a pest page.
  if (c.includes('pest') || c.includes('insect') || c.includes('grub')) {
    tools.push({ href: '/tools/grub-control-calculator', label: 'Grub Control Calculator' })
    tools.push({ href: '/tools/soil-temperature', label: 'Soil Temperature Tracker' })
  }
  if (c.includes('weed')) {
    tools.push({ href: '/tools/herbicide-timing', label: 'Herbicide Timing Calculator' })
  }
  if (c.includes('fertiliz') || c.includes('soil')) {
    tools.push({ href: '/tools/fertilizer-calculator', label: 'Fertilizer Calculator' })
  }
  if (c.includes('water') || c.includes('irrigation')) {
    tools.push({ href: '/tools/watering-schedule', label: 'Watering Schedule Generator' })
  }
  if (c.includes('mowing') || c.includes('maintenance')) {
    tools.push({ href: '/tools/mowing-calculator', label: 'Mowing Height Calculator' })
  }
  if (c.includes('grass type') || c.includes('identification')) {
    tools.push({ href: '/tools/grass-comparison', label: 'Grass Comparison Tool' })
  }
  if (c.includes('seasonal') || c.includes('winter') || c.includes('spring')) {
    tools.push({ href: '/tools/soil-temperature', label: 'Soil Temperature Tracker' })
  }

  // Pad with popular tools if fewer than 2 matched (exclude Grass ID — already shown in sticky CTA above)
  if (tools.length < 2) {
    const existingHrefs = new Set(tools.map(t => t.href))
    const fallbacks: { href: string; label: string }[] = [
      { href: '/tools/fertilizer-calculator', label: 'Fertilizer Calculator' },
      { href: '/tools/watering-schedule', label: 'Watering Schedule' },
      { href: '/tools/herbicide-timing', label: 'Herbicide Timing Calculator' },
      { href: '/tools/mowing-calculator', label: 'Mowing Height Calculator' },
      { href: '/tools/soil-temperature', label: 'Soil Temperature Tracker' },
    ]
    for (const fb of fallbacks) {
      if (tools.length >= 3) break
      if (!existingHrefs.has(fb.href)) {
        tools.push(fb)
        existingHrefs.add(fb.href)
      }
    }
  }

  // A category can match two keyword branches that push the same tool (e.g.
  // "seasonal pest control" hits both the pest branch and the seasonal branch),
  // which would render the same row twice.
  const seen = new Set<string>()
  return tools.filter((t) => (seen.has(t.href) ? false : (seen.add(t.href), true))).slice(0, 3)
}

const GRASS_GUIDE_SLUGS: { match: string; slug: string; label: string }[] = [
  { match: 'kentucky bluegrass', slug: 'kentucky-bluegrass', label: 'Kentucky Bluegrass' },
  { match: 'tall fescue', slug: 'tall-fescue', label: 'Tall Fescue' },
  { match: 'fine fescue', slug: 'fine-fescue', label: 'Fine Fescue' },
  { match: 'perennial ryegrass', slug: 'perennial-ryegrass', label: 'Perennial Ryegrass' },
  { match: 'bermuda', slug: 'bermudagrass', label: 'Bermudagrass' },
  { match: 'zoysia', slug: 'zoysiagrass', label: 'Zoysiagrass' },
  { match: 'st. augustine', slug: 'st-augustinegrass', label: 'St. Augustinegrass' },
  { match: 'st augustine', slug: 'st-augustinegrass', label: 'St. Augustinegrass' },
  { match: 'centipede', slug: 'centipede', label: 'Centipede Grass' },
  { match: 'bahia', slug: 'bahiagrass', label: 'Bahiagrass' },
  { match: 'buffalo', slug: 'buffalograss', label: 'Buffalograss' },
]

export function getRelatedGuide(category: string, tags?: string[]): { href: string; label: string } {
  const haystack = [category, ...(tags || [])].join(' ').toLowerCase()
  // Grass-type guides take precedence — most specific match wins.
  for (const g of GRASS_GUIDE_SLUGS) {
    if (haystack.includes(g.match)) {
      return { href: `/guides/${g.slug}`, label: `${g.label} Care Guide` }
    }
  }
  // Care-task pillar fallback.
  if (haystack.includes('fertiliz') || haystack.includes('soil')) {
    return { href: '/guides/fertilizing', label: 'Lawn Fertilizing Guide' }
  }
  if (haystack.includes('weed') || haystack.includes('herbicide') || haystack.includes('pest')) {
    return { href: '/guides/weed-control', label: 'Lawn Weed Control Guide' }
  }
  if (haystack.includes('water') || haystack.includes('irrigation') || haystack.includes('drought')) {
    return { href: '/guides/watering', label: 'Lawn Watering Guide' }
  }
  if (haystack.includes('mow') || haystack.includes('maintenance') || haystack.includes('cutting')) {
    return { href: '/guides/mowing', label: 'Lawn Mowing Guide' }
  }
  // Final fallback — hub page.
  return { href: '/guides', label: 'All Lawn Care Guides' }
}

interface RelatedPost {
  id: string
  title: string
  excerpt?: string
  category: string
  date_published: string
  image?: string
  featured?: boolean
  slug: string
  reading_time?: number
}

interface AdjacentPost {
  slug: string
  title: string
  /** Directus file UUID for the post's cover image, when set. */
  image?: string | null
}

interface BlogPostFooterSectionsProps {
  relatedPosts: RelatedPost[]
  category: string
  slug?: string
  tags?: string[]
  previousPost: AdjacentPost | null
  nextPost: AdjacentPost | null
  author: Author
}


export function BlogPostFooterSections({
  relatedPosts,
  category,
  slug,
  tags,
  previousPost,
  nextPost,
  author,
}: BlogPostFooterSectionsProps) {
  // Map blog categories to relevant product categories in Directus
  const productCategory = mapBlogToProductCategory(category, slug)
  const relatedGuide = getRelatedGuide(category, tags)
  // Symptom/problem posts get a post-read diagnose card (highest-converting
  // capture surface; July 2026 audit) above the newsletter capture.
  const showDiagnoseCard = isDiagnosisPost(slug, category, tags)
  const showWeedCard = isWeedPost(slug, category, tags)

  // Post-read slot routing (reworked 2026-07-22) — see resolvePostReadSurface
  // for the precedence and the data behind it.
  const postReadSurface = resolvePostReadSurface(slug ?? '', category, tags, {
    isDiagnosis: showDiagnoseCard,
    isWeed: showWeedCard,
  })

  return (
    <div className="relative px-4 md:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mt-12">
          <LazyProductRecommendations category={productCategory} articleSlug={slug} tags={tags} limit={3} />
        </div>

        <div className="mt-12">
          <Link
            href={relatedGuide.href}
            prefetch={false}
            className="block group"
          >
            <div className="ds-glass rounded-[20px] p-5 md:p-6 flex items-center gap-4 transition-[transform,box-shadow] duration-300 hover:-translate-y-1">
              <div className="flex-shrink-0 grid place-items-center w-12 h-12 rounded-2xl border border-white/70 bg-green-50/85 text-green-700 transition-colors group-hover:bg-green-100/90">
                <BookOpen className="w-6 h-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1">
                  Read the full guide
                </div>
                <div className="font-editorial text-base md:text-lg font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                  {relatedGuide.label}
                </div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 flex-shrink-0 transition-colors" />
            </div>
          </Link>
        </div>

        {/* Post-read peak: ONE surface, matched to why the reader is here.
            Peak-end + specificity rules — obsidian/research/growth-design/. */}
        {(showDiagnoseCard || showWeedCard) && (
          <div className="mt-12 max-w-3xl mx-auto">
            <PostReadDiagnoseCTA weed={showWeedCard} slug={slug} />
          </div>
        )}

        {postReadSurface === 'scan' && (
          <div className="mt-12 max-w-3xl mx-auto">
            <InlineScanCta
              ctaLocation={IDENTIFY_CTA.location.blogPostReadIdentify}
              heading="Still not sure this is your grass?"
              tagline="Snap a photo and get the answer plus a care plan for your ZIP"
            />
          </div>
        )}


        {/* Web → App install CTA. Self-gates to null until the app is live
            (NEXT_PUBLIC_MOBILE_APP_PROMO_LIVE + App Store URL), so this is inert
            until launch. Placed at the post-read peak — these guides draw the
            grass-ID demand cluster the app's hero feature serves.
            See obsidian/features/web-to-app-funnel.md. */}
        <div className="mt-12 max-w-3xl mx-auto">
          <AppDownloadCTA placement="blog_footer" variant="card" />
        </div>

        {relatedPosts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-12 text-center">
              Related Articles
            </h2>
            <ResponsiveBlogCards
              posts={relatedPosts.map((post) => ({
                id: post.id,
                title: post.title,
                excerpt:
                  post.excerpt ||
                  `Learn more about ${post.category.toLowerCase()} for your lawn.`,
                category: post.category,
                date_published: post.date_published,
                image: getDirectusImageUrl(post.image),
                featured: post.featured,
                slug: post.slug,
                reading_time: post.reading_time || 5,
              }))}
            />
          </div>
        )}

        <div className="mt-12">
          <AuthorBioCard author={author} />
        </div>

        {(previousPost || nextPost) && (
          <div
            className={`flex flex-col sm:flex-row items-stretch sm:items-stretch gap-3 sm:gap-4 mt-8 mb-12 ${
              previousPost && nextPost ? "justify-between" : "justify-center"
            }`}
          >
            {previousPost && (
              <Link
                href={`/blog/${previousPost.slug}`}
                prefetch={false}
                className={`group min-w-0 ${
                  previousPost && nextPost
                    ? "flex-1"
                    : "flex-1 max-w-xl"
                }`}
              >
                <div className="ds-glass rounded-[18px] w-full h-full p-4 md:p-5 lg:p-6 flex items-center text-left transition-[transform,box-shadow] duration-300 hover:-translate-y-1">
                  <div className="flex items-center mr-3 md:mr-4 flex-shrink-0">
                    <div className="grid place-items-center w-9 h-9 md:w-10 md:h-10 rounded-2xl border border-white/70 bg-green-50/85 text-green-700 transition-colors group-hover:bg-green-100/90">
                      <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                  </div>
                  {previousPost.image && (
                    <img
                      src={getDirectusImageUrl(previousPost.image, 96, 96)}
                      alt=""
                      width={48}
                      height={48}
                      loading="lazy"
                      className="w-10 h-10 md:w-12 md:h-12 rounded-xl object-cover border border-white/70 flex-shrink-0 mr-3 md:mr-4"
                    />
                  )}
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="text-xs font-bold text-green-700 mb-1 uppercase tracking-wide">
                      <span>Previous Article</span>
                    </div>
                    <div className="font-editorial text-sm md:text-base font-semibold text-gray-900 group-hover:text-green-700 transition-colors duration-200 leading-tight truncate">
                      {previousPost.title.length > 35
                        ? `${previousPost.title.substring(0, 35)}...`
                        : previousPost.title}
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {nextPost && (
              <Link
                href={`/blog/${nextPost.slug}`}
                prefetch={false}
                className={`group min-w-0 ${
                  previousPost && nextPost
                    ? "flex-1 sm:ml-4"
                    : "flex-1 max-w-xl"
                }`}
              >
                <div className="ds-glass rounded-[18px] w-full h-full p-4 md:p-5 lg:p-6 flex flex-row-reverse items-center text-right transition-[transform,box-shadow] duration-300 hover:-translate-y-1">
                  <div className="flex items-center ml-3 md:ml-4 flex-shrink-0">
                    <div className="grid place-items-center w-9 h-9 md:w-10 md:h-10 rounded-2xl border border-white/70 bg-green-50/85 text-green-700 transition-colors group-hover:bg-green-100/90">
                      <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                  </div>
                  {nextPost.image && (
                    <img
                      src={getDirectusImageUrl(nextPost.image, 96, 96)}
                      alt=""
                      width={48}
                      height={48}
                      loading="lazy"
                      className="w-10 h-10 md:w-12 md:h-12 rounded-xl object-cover border border-white/70 flex-shrink-0 ml-3 md:ml-4"
                    />
                  )}
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="text-xs font-bold text-green-700 mb-1 uppercase tracking-wide">
                      <span>Next Article</span>
                    </div>
                    <div className="font-editorial text-sm md:text-base font-semibold text-gray-900 group-hover:text-green-700 transition-colors duration-200 leading-tight truncate">
                      {nextPost.title.length > 35
                        ? `${nextPost.title.substring(0, 35)}...`
                        : nextPost.title}
                    </div>
                  </div>
                </div>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
